import React from 'react'
import PropTypes from 'prop-types'
import { RendererContext } from 'react-fela'
import Visible from './Visible'

const cdnUrl = '//in.bmscdn.com'
const baseCdnPath = 'webin'
const noEventPreviewImageUrl = `${cdnUrl}/${baseCdnPath}/test-m6/placeholders/event-placeholder.jpg`

const __keyframe = () => ({
	'0%': {
		transform: 'scale(1.01)',
		opacity: '0',
	},
	'100%': {
		transform: 'scale(1)',
		opacity: '1',
	},
})

const __image = ({ isDesktop, customStyle, ...props }) => {
	const { preview, keyframeIn, height, width } = props

	return {
		display: 'block',
		width,
		height,
		margin: '0 auto',
		border: '0 none',
		borderRadius: 'inherit',
		extend: [
			{
				condition: preview === true,
				style: {
					filter: 'blur(1px)',
					transform: 'scale(1.05)',
				},
			},
			{
				condition: preview === false,
				style: {
					left: '0',
					top: '0',
					width,
					willChange: 'transform, opacity',
					animationName: `${keyframeIn}`,
					animationDuration: '1s',
					animationTimingFunction: 'ease-out',
					animationDelay: 'initial',
				},
			},
			{
				condition: customStyle,
				style: customStyle,
			},
		],
	}
}

export const defaultPlaceHolder = `${cdnUrl}/webin/test-m6/default-src.jpg`

const LazyImage = ({
	src,
	tinySrc,
	srcErr,
	alt,
	onClick,
	inheritDimension,
	id,
	isDesktop,
	customStyle,
	height = '100%',
	width = '100%',
}) => (
	<Visible>
		<Img
			{...{
				src,
				tinySrc,
				srcErr,
				alt,
				onClick,
				inheritDimension,
				id,
				isDesktop,
				customStyle,
				height,
				width,
			}}
		/>
	</Visible>
)

export class Img extends React.Component {
	constructor(props) {
		super(props)
		this.state = {
			src: props.tinySrc || '',
			imageRendered: false,
			alt: props.alt || 'placeholder',
			keyframeIn: '',
		}

		this.img = null
	}

	componentDidMount() {
		// initial checkup with on componentDidMount. If it were visible, already would receive the state.
		this.initializeImage(this.props)
	}

	componentWillUnmount() {
		this.img = null
	}

	initializeImage = ({ src, alt, srcErr, visible }) => {
		if (!visible) {
			return
		}
		const renderer = this.context
		this.img = document.createElement('img')
		// when image loads, push actual URL to state
		this.img.onload = () => {
			if (this.img)
				this.setState({
					src,
					keyframeIn: renderer.renderKeyframe(__keyframe),
					alt,
					imageRendered: true,
				})
		}
		this.img.onerror = () => {
			if (this.img)
				this.setState({
					src: srcErr || noEventPreviewImageUrl,
					keyframeIn: renderer.renderKeyframe(__keyframe),
					alt,
					imageRendered: true,
				})
		}
		this.img.src = src || ''
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(nextProps) {
		const { src, alt, tinySrc } = this.props
		const { imageRendered, src: srcInState } = this.state

		if (
			nextProps.visible ||
			nextProps.src !== src ||
			nextProps.alt !== alt ||
			nextProps.tinySrc !== tinySrc
		) {
			// looks like the values are different. Now re-render based on new props.
			const isVisible = nextProps.visible
			const wasLoadedPreviously = imageRendered
			const isSameAsLoaded =
				srcInState === nextProps.src || srcInState === nextProps.srcErr
			if (isVisible && !wasLoadedPreviously) {
				// first time it became visible. Load it a fresh.
				this.initializeImage(nextProps)
			} else if (isVisible && wasLoadedPreviously && !isSameAsLoaded) {
				// This was already loaded. Now reset and reload.
				this.setState(
					{
						src: nextProps.tinySrc || defaultPlaceHolder,
						imageRendered: false,
						alt: nextProps.alt || 'placeholder',
						keyframeIn: '',
					},
					() => this.initializeImage(nextProps)
				)
			}
		}
	}

	render() {
		const renderer = this.context
		const {
			inheritDimension,
			onClick,
			id,
			isDesktop,
			customStyle,
			height,
			width,
		} = this.props
		const { keyframeIn, imageRendered, src, alt } = this.state
		const imgClass = renderer.renderRule(__image, {
			keyframeIn,
			preview: !imageRendered,
			isDesktop,
			customStyle,
			height,
			width,
		})
		let wrapperStyles = {}
		// when you need the image to inherit the dimensions
		if (inheritDimension) {
			wrapperStyles = {
				height: 'inherit',
				width: 'inherit',
			}
		}

		if (customStyle) {
			wrapperStyles = {
				...wrapperStyles,
				...customStyle,
			}
		}

		return (
			<div role="presentation" style={wrapperStyles} onClick={onClick}>
				<img className={imgClass} src={src} alt={`${alt}`} id={id} />
			</div>
		)
	}
}

Img.contextType = RendererContext

LazyImage.propTypes = {
	src: PropTypes.string,
	tinySrc: PropTypes.string,
	srcErr: PropTypes.string,
	alt: PropTypes.string,
	onClick: PropTypes.func,
	inheritDimension: PropTypes.bool,
	isDesktop: PropTypes.bool,
}

LazyImage.defaultProps = {
	isDesktop: false,
}

export default LazyImage
